/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { RichTextComponent } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { SubscriptionLine } from './components/subscription-line';
import { ISubscriptionManagementData } from './subscription-management.data';
import { ISubscriptionManagementProps } from './subscription-management.props.autogenerated';

export interface ISubscriptionManagementViewProps extends ISubscriptionManagementProps<ISubscriptionManagementData> { }

/**
 *
 * SubscriptionManagement component
 * @extends {React.Component<ISubscriptionManagementProps<ISubscriptionManagementData>>}
 */
@observer
class SubscriptionManagement extends React.Component<ISubscriptionManagementProps<ISubscriptionManagementData>> {

    @observable private _message: string = '';

    constructor(props: ISubscriptionManagementProps<ISubscriptionManagementData>) {
        super(props);

        this._setMessage = this._setMessage.bind(this);
        this._unsetMessage = this._unsetMessage.bind(this);
    }

    public render(): JSX.Element | null {
        if (!this.props.data.subscriptions.result || !this.props.data.creditCards.result || !this.props.data.addresses.result) { return null; }
        const subscriptions = this.props.data.subscriptions.result;
        const cards = this.props.data.creditCards.result;
        const addresses = this.props.data.addresses.result;
        const { className, description } = this.props.config;
        return (
            <div className={classnames('subscription-management', className)}>
                {this._message && <div className='subscription-management-message'>{this._message}</div>}
                {
                    subscriptions.Result && subscriptions.Result.length > 0 ? (
                        <div className='subscription-management-table'>
                            {this._renderHeader()}
                            {subscriptions.Result.map((line) =>
                                <SubscriptionLine
                                    key={line.SubscriptionID}
                                    line={line}
                                    cards={cards}
                                    onError={this._setMessage}
                                    onSuccess={this._setMessage}
                                    addresses={addresses}
                                    managementProps={this.props}
                                />
                            )}
                        </div>
                    ) : this._renderNoSubscription()
                }
                {description && <RichTextComponent text={description || ''} className='subscription-management-description' />}
            </div>
        );
    }

    private _setMessage(message: string): void {
        this._message = message;
    }

    private _unsetMessage(): void {
        this._message = '';
    }

    private _renderHeader(): JSX.Element {
        const {
            subManagement_statusCol,
            subManagement_subCol,
            subManagement_freqCol,
            subManagement_deliveryCol,
            subManagement_actionCol
        } = this.props.resources;
        return (
            <div className='subscription-management-header'>
                <div className='subscription-management-status'>{subManagement_statusCol}</div>
                <div className='subscription-management-subscription'>{subManagement_subCol}</div>
                <div className='subscription-management-frequency'>{subManagement_freqCol}</div>
                <div className='subscription-management-delivery'>{subManagement_deliveryCol}</div>
                <div className='subscription-management-actions'>{subManagement_actionCol}</div>
            </div>
        );
    }

    private _renderNoSubscription(): JSX.Element {
        const { subscribeLink } = this.props.config;
        const { subManagement_noSubscription } = this.props.resources;
        return (
            <div className='subscription-management-empty'>
                <div className='subscription-management-empty-text'>
                    {subManagement_noSubscription}
                </div>
                {subscribeLink?.linkUrl?.destinationUrl &&
                    <a
                        className='subscription-management-empty-button'
                        href={subscribeLink.linkUrl.destinationUrl}
                        target={subscribeLink.openInNewTab ? '_blank' : undefined}
                        aria-label={subscribeLink.ariaLabel}
                        role='button'
                        rel="noreferrer"
                    >
                        {subscribeLink.linkText}
                    </a>
                }
            </div>
        );
    }
}

export default SubscriptionManagement;
